import { Package, Eye, Boxes, AlertTriangle, Search, Plus, Sparkles, FileSpreadsheet, LayoutGrid, Rows3, CarFront, Building2 } from "lucide-react"
import { useEffect, useMemo, useState, type ReactNode } from "react"
import * as XLSX from "xlsx"
import StockDetailsModal from "./StockDetailsModal"
import { useDispatch, useSelector } from "react-redux"
import { stockSummaryData } from "../../store/actions"
import { canAddProduct, getCurrentPlan, getMaxProducts } from "../../utils/permissions"
import { getGeneralProductsCatalog } from "../../api/products/get"
import toast from "react-hot-toast"
import { getStoreExperience } from "../../utils/storeExperience"
import { useNavigate } from "react-router-dom"

type StockRow = {
  avgBuyPrice?: number
  identifier?: string
  isLowStock?: boolean
  name?: string
  sellPrice: any
  model: string
  quantity: number
  stockAlertThreshold?: number
  totalBuyValue: number
}

type StockStats = {
  totalPhones: number
  totalModels: number
  totalBuyValue: number
  lowStockCount: number
  threshold: number
}

type ViewMode = "table" | "cards"

export default function Stock() {
  const { userdata, stockdata } = useSelector((state: any) => state.UserReducer)
  const currentStore = userdata?.store
  const currentPlan = getCurrentPlan(userdata)
  const experience = getStoreExperience(currentStore)
  const navigate = useNavigate()
  const isCarStore = experience.kind === "car"
  const isPropertyStore = experience.kind === "property"
  const isGeneralStore = experience.kind === "general"
  const isServiceStore = experience.kind === "service"

  const dispatch = useDispatch()

  const [page, setPage] = useState(1)
  const limit = 5
  const [query, setQuery] = useState("")
  const [showLowStockOnly, setShowLowStockOnly] = useState(false)
  const [detailsOpen, setDetailsOpen] = useState(false)
  const [selectedModel, setSelectedModel] = useState<string | null>(null)
  const [exporting, setExporting] = useState(false)
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== "undefined" ? window.innerWidth < 768 : false
  )
  const [viewMode, setViewMode] = useState<ViewMode>("cards")

  const pagination = stockdata?.pagination || {}
  const { totalPages = 1, page: currentPage = 1 } = pagination

  const [rows, setRows] = useState<StockRow[]>(stockdata?.data || [])
  const [stats, setStats] = useState<StockStats>(
    stockdata?.stats || {
      totalPhones: 0,
      totalModels: 0,
      totalBuyValue: 0,
      lowStockCount: 0,
      threshold: 2,
    }
  )

  const refreshStock = () => {
    if (!currentStore?._id) return

    dispatch(
      stockSummaryData(currentStore._id, {
        q: query,
        lowOnly: showLowStockOnly,
        threshold: 2,
        page,
        limit,
      })
    )
  }

  const lowStockThreshold = stats.threshold || 2
  const maxQuantity = Math.max(1, ...rows.map((m) => m.quantity))
  const totalProducts = Number(stats.totalModels || 0)
  const maxProducts = getMaxProducts(currentPlan)
  const canAddMoreProducts = canAddProduct(currentPlan, totalProducts)
  const remainingProductsSlots =
    maxProducts === null ? null : Math.max(0, maxProducts - totalProducts)

  const filteredStock = useMemo(() => {
    const base = rows.filter((m) => m.model.toLowerCase().includes(query.trim().toLowerCase()))
    if (showLowStockOnly) return base.filter((m) => isRowLowStock(m, lowStockThreshold))
    return base
  }, [query, showLowStockOnly, rows, lowStockThreshold])

  const itemSingular = experience.inventorySingular
  const itemPlural = experience.inventoryPlural
  const stockFilterLabel = isPropertyStore ? "Disponibilite faible" : "Faible stock"
  const kpiLabels = {
    totalUnits: isCarStore
      ? "Vehicules en parc"
      : isPropertyStore
        ? "Biens disponibles"
        : isServiceStore
          ? "Services catalogues"
        : "Produits en stock",
    totalModels: isCarStore
      ? "References auto"
      : isPropertyStore
        ? "Mandats actifs"
        : isServiceStore
          ? "Offres publiees"
        : "Modeles disponibles",
    totalBuyValue: isCarStore
      ? "Capital engage"
      : isPropertyStore
        ? "Valeur acquisition"
        : isServiceStore
          ? "Valeur catalogue"
        : "Valeur totale d'achat",
  }

  useEffect(() => {
    if (typeof window === "undefined") return

    const handleResize = () => setIsMobile(window.innerWidth < 768)
    handleResize()
    window.addEventListener("resize", handleResize)
    return () => window.removeEventListener("resize", handleResize)
  }, [])

  useEffect(() => {
    setViewMode(isGeneralStore && !isMobile ? "table" : "cards")
  }, [isGeneralStore, isMobile])

  useEffect(() => {
    if (!currentStore?._id) return

    dispatch(
      stockSummaryData(currentStore._id, {
        q: query,
        lowOnly: showLowStockOnly,
        threshold: 2,
        page,
        limit,
      })
    )
  }, [currentStore?._id, query, showLowStockOnly, page, limit, dispatch])

  useEffect(() => {
    if (stockdata) {
      setRows(stockdata?.data)
      setStats(stockdata?.stats)
    }
  }, [stockdata])

  const exportReferencesExcel = async () => {
    if (!currentStore?._id) return

    try {
      setExporting(true)

      const allRows = await getGeneralProductsCatalog(currentStore._id, query)
      const exportRows = Array.isArray(allRows) ? allRows : []

      if (exportRows.length === 0) {
        toast.error("Aucune référence à exporter")
        return
      }

      const sheetRows = exportRows.map((item: any, index: number) => ({
        No: index + 1,
        Boutique: currentStore?.name || "-",
        Produit: item?.name || "-",
        Reference: item?.identifier || "-",
        QuantiteEnStock: Number(item?.quantityInStock ?? 0),
      }))

      const worksheet = XLSX.utils.json_to_sheet(sheetRows)
      const workbook = XLSX.utils.book_new()
      XLSX.utils.book_append_sheet(workbook, worksheet, "References")

      const dateStamp = new Date().toISOString().slice(0, 10)
      const storeSlug = String(currentStore?.name || "store")
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-+|-+$/g, "")

      XLSX.writeFile(workbook, `references-${storeSlug || "store"}-${dateStamp}.xlsx`)
      toast.success("Références exportées avec succès")
    } catch (e: any) {
      toast.error(e?.message || "Impossible d'exporter les références")
    } finally {
      setExporting(false)
    }
  }

  return (
    <>
      <div className="space-y-8">
        <section className={`relative overflow-hidden rounded-3xl border border-white/40 bg-gradient-to-br ${experience.stock.heroClass} p-6 text-white shadow-xl sm:p-7`}>
          <div className={`pointer-events-none absolute -top-14 -right-10 h-36 w-36 rounded-full blur-3xl ${experience.stock.heroGlowClass}`} />
          <div className="pointer-events-none absolute -bottom-12 -left-8 h-32 w-32 rounded-full bg-blue-200/20 blur-3xl" />

          <div className="relative flex flex-col gap-4 xl:flex-row xl:items-end xl:justify-between">
            <div>
              <p className="inline-flex items-center gap-1 rounded-full bg-white/20 px-2.5 py-1 text-[11px] uppercase tracking-[0.16em]">
                <Sparkles size={12} /> {experience.stock.heroBadge}
              </p>
              <h1 className="mt-3 text-2xl font-bold sm:text-3xl">{experience.stock.heroTitle}</h1>
              <p className="mt-2 max-w-2xl text-sm text-white/85">{experience.stock.heroSubtitle}</p>
            </div>

            <div className="flex w-full flex-col gap-3 sm:w-auto sm:flex-row">
              <div className="relative">
                <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder={experience.searchPlaceholder}
                  className="w-full sm:w-64 pl-10 pr-4 py-2.5 rounded-xl border border-white/30 bg-white/95 text-slate-800 placeholder:text-slate-400 focus:ring-2 focus:ring-blue-200 outline-none"
                />
              </div>

              <div className="inline-flex w-full rounded-xl border border-white/20 bg-white/10 p-1 backdrop-blur-sm sm:w-auto">
                <button
                  onClick={() => setViewMode("cards")}
                  className={`inline-flex flex-1 items-center justify-center gap-2 rounded-lg px-3 py-2 text-sm transition sm:flex-none ${viewMode === "cards" ? "bg-white text-slate-900" : "text-white/85 hover:bg-white/15"}`}
                >
                  <LayoutGrid size={16} />
                  Cartes
                </button>
                <button
                  onClick={() => setViewMode("table")}
                  className={`inline-flex flex-1 items-center justify-center gap-2 rounded-lg px-3 py-2 text-sm transition sm:flex-none ${viewMode === "table" ? "bg-white text-slate-900" : "text-white/85 hover:bg-white/15"}`}
                >
                  <Rows3 size={16} />
                  Tableau
                </button>
              </div>

              <button
                onClick={() => {
                  if (canAddMoreProducts) {
                    navigate("/stock/new", {
                      state: { currentProductsCount: totalProducts },
                    })
                  }
                }}
                disabled={!canAddMoreProducts}
                className={`inline-flex w-full items-center justify-center gap-2 rounded-xl px-4 py-2.5 font-semibold transition disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto ${experience.stock.buttonClass}`}
              >
                <Plus size={16} />
                {canAddMoreProducts ? (isCarStore ? "Nouveau vehicule" : isPropertyStore ? "Nouveau bien" : isServiceStore ? "Nouveau service" : "Nouveau produit") : "Limite FREE atteinte"}
              </button>
            </div>
          </div>

          <div className="relative mt-4 inline-flex rounded-xl border border-white/25 bg-white/10 p-1 backdrop-blur-sm">
            <button
              onClick={() => setShowLowStockOnly(false)}
              className={`px-3 py-2 text-sm rounded-lg transition ${!showLowStockOnly ? "bg-white text-blue-700" : "text-white/85 hover:bg-white/15"
                }`}
            >
              Tous
            </button>
            <button
              onClick={() => setShowLowStockOnly(true)}
              className={`px-3 py-2 text-sm rounded-lg transition ${showLowStockOnly ? "bg-white text-blue-700" : "text-white/85 hover:bg-white/15"
                }`}
            >
              {stockFilterLabel}
            </button>
          </div>
        </section>

        {maxProducts !== null && (
          <div
            className={`text-sm p-3 rounded-xl border ${canAddMoreProducts ? "bg-blue-50 text-blue-700 border-blue-100" : "bg-amber-50 text-amber-700 border-amber-200"
              }`}
          >
            Plan FREE: {totalProducts}/{maxProducts} produits utilisés
            {remainingProductsSlots !== null &&
              canAddMoreProducts &&
              ` • Il vous reste ${remainingProductsSlots} emplacement(s).`}
            {!canAddMoreProducts && " • Passez à un plan payant pour augmenter votre capacité."}
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <Kpi
            label={kpiLabels.totalUnits}
            value={stats.totalPhones}
            icon={isCarStore ? <CarFront size={18} /> : isPropertyStore ? <Building2 size={18} /> : <Package size={18} />}
            color={isCarStore ? "from-orange-500 to-amber-500" : isPropertyStore ? "from-emerald-500 to-teal-500" : "from-blue-600 to-cyan-600"}
            ring={isCarStore ? "ring-orange-100" : isPropertyStore ? "ring-emerald-100" : "ring-blue-100"}
          />
          <Kpi
            label={kpiLabels.totalModels}
            value={stats.totalModels}
            icon={<Boxes size={18} />}
            color={isCarStore ? "from-slate-800 to-orange-600" : isPropertyStore ? "from-teal-700 to-cyan-600" : "from-indigo-600 to-blue-700"}
            ring={isCarStore ? "ring-slate-200" : isPropertyStore ? "ring-teal-100" : "ring-indigo-100"}
          />
          <Kpi
            label={kpiLabels.totalBuyValue}
            value={`${Number(stats.totalBuyValue || 0).toLocaleString()} FCFA`}
            icon={<AlertTriangle size={18} />}
            color={isCarStore ? "from-orange-600 to-red-500" : isPropertyStore ? "from-emerald-600 to-lime-500" : "from-rose-600 to-red-500"}
            ring={isCarStore ? "ring-orange-100" : isPropertyStore ? "ring-emerald-100" : "ring-rose-100"}
          />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div className={`lg:col-span-2 rounded-3xl border p-5 shadow-sm ${experience.stock.panelClass}`}>
            <div className="flex items-center justify-between mb-3">
              <p className="text-sm font-semibold text-slate-800">{isPropertyStore ? "Disponibilite du portefeuille" : "Niveau du catalogue"}</p>
              <span className="text-xs text-slate-500">{stats.lowStockCount} {itemPlural} a surveiller</span>
            </div>

            {!stockdata ? (
              <div className="py-6 text-sm text-slate-500">Chargement…</div>
            ) : (
              <div className="space-y-3">
                {filteredStock.slice(0, 4).map((item) => (
                  <div key={`bar-${item.model}`} className="flex items-center gap-3">
                    <span className="text-xs text-slate-600 w-28 truncate">{item.model}</span>
                    <div className="flex-1 h-2 rounded-full bg-slate-100 overflow-hidden">
                      <div
                        className={`h-full rounded-full ${isRowLowStock(item, lowStockThreshold) ? "bg-orange-500" : experience.stock.progressClass}`}
                        style={{ width: `${Math.max(6, (item.quantity / maxQuantity) * 100)}%` }}
                      />
                    </div>
                    <span className="text-xs font-medium text-slate-700 w-10 text-right">{item.quantity}</span>
                  </div>
                ))}
                {filteredStock.length === 0 && <div className="text-sm text-slate-500">Aucun résultat</div>}
              </div>
            )}
          </div>

          <div className={`rounded-3xl border border-white/30 bg-gradient-to-br ${experience.stock.alertClass} p-5 text-white shadow-sm flex flex-col justify-between`}>
            <div>
              <p className="text-xs uppercase tracking-wide text-white/70">{isPropertyStore ? "Biens a relancer" : "Alerte catalogue"}</p>
              <p className="mt-2 text-2xl font-semibold">{stats.lowStockCount}</p>
              <p className="text-sm text-white/85">{itemPlural} a surveiller</p>
            </div>
            <p className="mt-6 text-xs text-white/80">
              {isPropertyStore
                ? "Conseil : les biens affiches ici suivent chacun leur seuil d'alerte configure."
                : "Conseil : chaque reference passe en alerte selon son propre seuil configure."}
            </p>
          </div>
        </div>

        {viewMode === "cards" ? (
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
            {!stockdata ? (
              <div className={`col-span-full rounded-2xl border p-6 text-center text-slate-500 shadow-sm ${experience.stock.panelClass}`}>Chargement du stock…</div>
            ) : (
              filteredStock.map((item) => (
                <StockCatalogCard
                  key={`card-${item.model}`}
                  item={item}
                  isCarStore={isCarStore}
                  isPropertyStore={isPropertyStore}
                  lowStockThreshold={lowStockThreshold}
                  lowStatusClass={experience.stock.lowStatusClass}
                  availableStatusClass={experience.stock.availableStatusClass}
                  panelMutedClass={experience.stock.panelMutedClass}
                  onOpen={() => {
                    setSelectedModel(item.model)
                    setDetailsOpen(true)
                  }}
                />
              ))
            )}
            {!!stockdata && filteredStock.length === 0 && (
              <div className={`col-span-full rounded-2xl border p-6 text-center text-slate-500 shadow-sm ${experience.stock.panelClass}`}>Aucun {itemSingular} disponible</div>
            )}
          </div>
        ) : (
          <div className={`overflow-hidden rounded-3xl border shadow-sm ${experience.stock.panelClass}`}>
            <div className={`border-b px-6 py-4 ${isGeneralStore ? "border-slate-100 bg-gradient-to-r from-slate-50 to-white" : "border-white/60 bg-white/80"}`}>
              <p className="text-sm font-semibold text-slate-800">
                {isCarStore ? "Vue tabulaire du parc" : isPropertyStore ? "Vue tabulaire du portefeuille" : "Detail du stock"}
              </p>
              <p className="mt-0.5 text-xs text-slate-500">
                {isCarStore
                  ? "Comparez rapidement les quantites, le capital et le prix de vente de vos vehicules."
                  : isPropertyStore
                    ? "Gardez une lecture compacte des biens, de leur disponibilite et des valeurs associees."
                    : "Consultez quantites, valeur achat et statut par modele."}
              </p>
            </div>

            {isMobile ? (
              <div className="space-y-3 p-4">
                {!stockdata ? (
                  <div className="rounded-2xl border border-slate-200 bg-white p-6 text-center text-slate-500">
                    Chargement du stock…
                  </div>
                ) : filteredStock.length === 0 ? (
                  <div className="rounded-2xl border border-slate-200 bg-white p-6 text-center text-slate-500">
                    Aucun {itemSingular} disponible
                  </div>
                ) : (
                  filteredStock.map((item) => (
                    <StockTableMobileCard
                      key={`table-mobile-${item.model}`}
                      item={item}
                      isCarStore={isCarStore}
                      isPropertyStore={isPropertyStore}
                      lowStockThreshold={lowStockThreshold}
                      lowStatusClass={experience.stock.lowStatusClass}
                      availableStatusClass={experience.stock.availableStatusClass}
                      stockFilterLabel={stockFilterLabel}
                      onOpen={() => {
                        setSelectedModel(item.model)
                        setDetailsOpen(true)
                      }}
                    />
                  ))
                )}
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-[760px] w-full text-sm">
                  <thead className="bg-slate-50">
                    <tr className="text-left text-slate-500">
                      <th className="px-6 py-3">{isPropertyStore ? "Bien" : "Modele"}</th>
                      <th className="px-6 py-3">{isPropertyStore ? "Disponibilite" : "Quantite"}</th>
                      <th className="px-6 py-3">Valeur achat</th>
                      <th className="px-6 py-3">{isPropertyStore ? "Prix affiche" : "Prix de vente"}</th>
                      <th className="px-6 py-3">Statut</th>
                      <th className="px-6 py-3 text-right">Actions</th>
                    </tr>
                  </thead>

                  <tbody>
                    {!stockdata ? (
                      <tr>
                        <td colSpan={6} className="px-6 py-10 text-center text-slate-500">
                          Chargement du stock…
                        </td>
                      </tr>
                    ) : (
                      filteredStock.map((item) => (
                        <tr key={item.model} className="border-t border-slate-100 transition hover:bg-slate-50/70">
                          <td className="px-6 py-4 font-medium text-slate-800">{item.model}</td>

                          <td className="px-6 py-4">
                            <span className="inline-flex items-center gap-2 text-slate-700">
                              {isCarStore ? <CarFront size={16} /> : isPropertyStore ? <Building2 size={16} /> : <Package size={16} />}
                              {item.quantity}
                            </span>
                          </td>

                          <td className="px-6 py-4 text-slate-600">{Number(item.totalBuyValue || 0).toLocaleString()} FCFA</td>

                          <td className="px-6 py-4 text-slate-600">
                            {item.sellPrice ? `${Number(item.sellPrice).toLocaleString()} FCFA` : "—"}
                          </td>

                          <td className="px-6 py-4">
                            <span
                              className={`inline-flex items-center rounded-full border px-2.5 py-1 text-xs font-medium ${isRowLowStock(item, lowStockThreshold)
                                  ? experience.stock.lowStatusClass
                                  : experience.stock.availableStatusClass
                                }`}
                            >
                              {isRowLowStock(item, lowStockThreshold) ? stockFilterLabel : "Disponible"}
                            </span>
                          </td>

                          <td className="px-6 py-4 text-right">
                            <button
                              onClick={() => {
                                setSelectedModel(item.model)
                                setDetailsOpen(true)
                              }}
                              className="inline-flex items-center gap-2 rounded-lg border border-slate-200 px-3 py-1.5 transition hover:bg-slate-100"
                            >
                              <Eye size={16} />
                              Voir details
                            </button>
                          </td>
                        </tr>
                      ))
                    )}

                    {!!stockdata && filteredStock.length === 0 && (
                      <tr>
                        <td colSpan={6} className="px-6 py-10 text-center text-slate-500">
                          Aucun {itemSingular} disponible
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {totalPages > 1 && (
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 pt-2">
            <p className="text-sm text-slate-500 text-center sm:text-left">
              Page <strong className="text-slate-700">{currentPage}</strong> sur <strong className="text-slate-700">{totalPages}</strong>
            </p>

            <div className="flex flex-col sm:flex-row gap-2">
              <button
                disabled={page === 1}
                onClick={() => setPage((p) => p - 1)}
                className="w-full sm:w-auto px-4 py-2 rounded-xl border border-slate-200 disabled:opacity-50 hover:bg-slate-100 transition"
              >
                Précédent
              </button>

              <button
                disabled={page === totalPages}
                onClick={() => setPage((p) => p + 1)}
                className="w-full sm:w-auto px-4 py-2 rounded-xl bg-blue-600 text-white disabled:opacity-50 hover:bg-blue-700 transition"
              >
                Suivant
              </button>
            </div>
          </div>
        )}

          <div className={`flex flex-col gap-3 rounded-2xl border p-4 shadow-sm sm:flex-row sm:items-center sm:justify-between ${experience.stock.panelClass}`}>
          <div>
            <p className="text-sm font-semibold text-slate-800">References {itemPlural}</p>
            <p className="mt-1 text-xs text-slate-500">
              Exportez la liste des references de la boutique active au format Excel.
            </p>
          </div>

          <button
            onClick={exportReferencesExcel}
            disabled={exporting || !currentStore?._id}
            className={`inline-flex items-center justify-center gap-2 rounded-xl border px-4 py-2.5 font-semibold transition disabled:cursor-not-allowed disabled:opacity-60 ${experience.stock.secondaryButtonClass}`}
          >
            <FileSpreadsheet size={16} />
            {exporting ? "Export..." : "Telecharger Excel"}
          </button>
        </div>
      </div>
      <StockDetailsModal
        isOpen={detailsOpen}
        storeId={currentStore?._id}
        model={selectedModel}
        onStockChanged={refreshStock}
        onClose={() => {
          setDetailsOpen(false)
          setSelectedModel(null)
        }}
      />
    </>
  )
}

function Kpi({
  label,
  value,
  icon,
  color,
  ring,
}: {
  label: string
  value: string | number
  icon?: ReactNode
  color: string
  ring: string
}) {
  return (
    <div className={`rounded-2xl border border-slate-200 bg-white p-5 shadow-sm ring-1 ${ring}`}>
      <div className="flex items-center gap-4">
        <div className={`h-10 w-10 rounded-xl bg-gradient-to-br ${color} text-white flex items-center justify-center shadow-lg`}>
          {icon}
        </div>
        <div>
          <p className="text-sm text-slate-500">{label}</p>
          <p className="text-2xl font-bold text-slate-800">{value}</p>
        </div>
      </div>
    </div>
  )
}

function StockCatalogCard({
  item,
  isCarStore,
  isPropertyStore,
  lowStockThreshold,
  lowStatusClass,
  availableStatusClass,
  panelMutedClass,
  onOpen,
}: {
  item: StockRow
  isCarStore: boolean
  isPropertyStore: boolean
  lowStockThreshold: number
  lowStatusClass: string
  availableStatusClass: string
  panelMutedClass: string
  onOpen: () => void
}) {
  const isLowStock = isRowLowStock(item, lowStockThreshold)
  const statusClass = isLowStock ? lowStatusClass : availableStatusClass
  const statusLabel = isLowStock
    ? isPropertyStore
      ? "Disponibilite faible"
      : "Faible stock"
    : "Disponible"

  return (
    <div className={`rounded-3xl border border-white/60 p-4 shadow-sm ${panelMutedClass}`}>
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-xs uppercase tracking-[0.18em] text-slate-400">
            {isCarStore ? "Vehicule" : isPropertyStore ? "Bien" : "Produit"}
          </p>
          <p className="mt-2 text-lg font-semibold text-slate-900">{item.model}</p>
        </div>
        <span className={`inline-flex items-center rounded-full border px-2.5 py-1 text-xs font-medium ${statusClass}`}>
          {statusLabel}
        </span>
      </div>

      <div className="mt-4 grid grid-cols-2 gap-3">
        <div className="rounded-2xl bg-white/80 p-3">
          <p className="text-xs text-slate-500">{isPropertyStore ? "Disponibles" : "En stock"}</p>
          <p className="mt-1 text-xl font-semibold text-slate-900">{item.quantity}</p>
        </div>
        <div className="rounded-2xl bg-white/80 p-3">
          <p className="text-xs text-slate-500">Prix vente</p>
          <p className="mt-1 text-sm font-semibold text-slate-900">
            {item.sellPrice ? `${Number(item.sellPrice).toLocaleString()} FCFA` : "—"}
          </p>
        </div>
      </div>

      <div className="mt-3 rounded-2xl bg-white/80 p-3">
        <p className="text-xs text-slate-500">Valeur achat</p>
        <p className="mt-1 text-sm font-semibold text-slate-900">
          {Number(item.totalBuyValue || 0).toLocaleString()} FCFA
        </p>
      </div>

      <div className="mt-4 flex items-center justify-between">
        <span className="inline-flex items-center gap-2 text-sm text-slate-700">
          {isCarStore ? <CarFront size={16} /> : isPropertyStore ? <Building2 size={16} /> : <Package size={16} />}
          {isPropertyStore ? "Fiche detaillee" : "Catalogue detaille"}
        </span>
        <button
          onClick={onOpen}
          className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-sm transition hover:bg-slate-100"
        >
          <Eye size={16} />
          Details
        </button>
      </div>
    </div>
  )
}

function StockTableMobileCard({
  item,
  isCarStore,
  isPropertyStore,
  lowStockThreshold,
  lowStatusClass,
  availableStatusClass,
  stockFilterLabel,
  onOpen,
}: {
  item: StockRow
  isCarStore: boolean
  isPropertyStore: boolean
  lowStockThreshold: number
  lowStatusClass: string
  availableStatusClass: string
  stockFilterLabel: string
  onOpen: () => void
}) {
  const isLowStock = isRowLowStock(item, lowStockThreshold)

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-xs uppercase tracking-[0.16em] text-slate-400">
            {isPropertyStore ? "Bien" : "Modele"}
          </p>
          <p className="mt-1 break-words text-base font-semibold text-slate-900">{item.model}</p>
        </div>
        <span
          className={`inline-flex shrink-0 items-center rounded-full border px-2.5 py-1 text-xs font-medium ${
            isLowStock ? lowStatusClass : availableStatusClass
          }`}
        >
          {isLowStock ? stockFilterLabel : "Disponible"}
        </span>
      </div>

      <div className="mt-4 grid grid-cols-2 gap-3">
        <MobileMetric
          label={isPropertyStore ? "Disponibilite" : "Quantite"}
          value={
            <span className="inline-flex items-center gap-2">
              {isCarStore ? <CarFront size={15} /> : isPropertyStore ? <Building2 size={15} /> : <Package size={15} />}
              {item.quantity}
            </span>
          }
        />
        <MobileMetric
          label={isPropertyStore ? "Prix affiche" : "Prix vente"}
          value={item.sellPrice ? `${Number(item.sellPrice).toLocaleString()} FCFA` : "—"}
        />
        <MobileMetric
          label="Valeur achat"
          value={`${Number(item.totalBuyValue || 0).toLocaleString()} FCFA`}
          className="col-span-2"
        />
      </div>

      <button
        onClick={onOpen}
        className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-xl border border-slate-200 px-4 py-2.5 text-sm font-medium text-slate-700 transition hover:bg-slate-50"
      >
        <Eye size={16} />
        Voir details
      </button>
    </div>
  )
}

function MobileMetric({
  label,
  value,
  className = "",
}: {
  label: string
  value: ReactNode
  className?: string
}) {
  return (
    <div className={`rounded-2xl bg-slate-50 p-3 ${className}`}>
      <p className="text-xs text-slate-500">{label}</p>
      <div className="mt-1 text-sm font-semibold text-slate-900">{value}</div>
    </div>
  )
}

function isRowLowStock(item: StockRow, fallbackThreshold: number) {
  if (typeof item.isLowStock === "boolean") return item.isLowStock

  const customThreshold = Number(item.stockAlertThreshold || 0)
  const threshold = customThreshold > 0 ? customThreshold : fallbackThreshold

  return Number(item.quantity || 0) <= threshold
}
